import { useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { DrawerScrollContainer, PanelContainer } from '@/components/ui'
import { useFacilityArchivedFloorsQuery } from '@/graphql/generated/hooks'

import { FloorI } from '../../types/types'
import {
  mapFloor,
  sortFloorsByDisplayOrderThenName,
} from '../../utils/mappedFacilityData'
import { FloorList } from './FloorList'
import { FloorListToggle } from './FloorListToggle'
import { FloorsPanelHeader } from './FloorsPanelHeader'

export interface FloorsPanelIProps {
  floors: FloorI[]
  currentFloorId?: string
  facilityId?: string
  onReorderFloors?: (floorIds: string[]) => void
}

export const FloorsPanel = ({
  floors,
  currentFloorId,
  facilityId,
  onReorderFloors,
}: FloorsPanelIProps) => {
  const { t } = useTranslation()
  const { shouldEnableUpdateFloor } = usePermissions()

  const isEditMode = useIsEditModeSatisfied()

  const isArchiveFloorEnabled = useFeatureFlag(
    FeatureFlag.archiveFloorAndFacility
  )

  const [shouldFilterFloorsWithIncidents, setFilterFloorsWithIncidents] =
    useState(false)
  const [shouldShowArchivedOnly, setShowArchivedOnly] = useState(false)

  const onFilterToggle = () => {
    setFilterFloorsWithIncidents(!shouldFilterFloorsWithIncidents)
  }

  const onArchivedToggle = () => {
    setShowArchivedOnly(!shouldShowArchivedOnly)
  }

  // The panel's regular floors query filters to archived: false, so the
  // toggle opts into archived floors with its own query.
  const { data: archivedFloorsData } = useFacilityArchivedFloorsQuery({
    skip: !isArchiveFloorEnabled || !shouldShowArchivedOnly || !facilityId,
    variables: { facilityId },
    fetchPolicy: 'network-only',
  })

  const archivedFloors = useMemo(
    () =>
      (archivedFloorsData?.facility?.floors?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(Boolean)
        .sort(sortFloorsByDisplayOrderThenName)
        .map((floor) =>
          mapFloor(floor, archivedFloorsData?.facility?.defaultFloorId)
        ),
    [archivedFloorsData]
  )

  const baseFloors = shouldShowArchivedOnly ? archivedFloors : floors
  const filteredFloors = shouldFilterFloorsWithIncidents
    ? baseFloors?.filter((i) => i.hasIncidents)
    : baseFloors

  const canReorderFloors =
    shouldEnableUpdateFloor &&
    isEditMode &&
    !shouldFilterFloorsWithIncidents &&
    !shouldShowArchivedOnly

  return (
    <PanelContainer w='390px'>
      <FloorsPanelHeader />
      <DrawerScrollContainer px='30px' py='20px'>
        <FloorListToggle
          checked={shouldFilterFloorsWithIncidents}
          onToggle={onFilterToggle}
        />
        {isArchiveFloorEnabled && (
          <FloorListToggle
            checked={shouldShowArchivedOnly}
            dataTestId='floorPlanPage_floorsPanel_archivedToggle'
            label={t('floors:floors_panel_show_archived_toggle')}
            onToggle={onArchivedToggle}
          />
        )}
        <FloorList
          canReorderFloors={canReorderFloors}
          currentFloorId={currentFloorId}
          floors={filteredFloors}
          onReorderFloors={onReorderFloors}
        />
      </DrawerScrollContainer>
    </PanelContainer>
  )
}
